'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback as useDebouncedCallbackOriginal } from 'use-debounce';
import type { DeferredFunction, DeferredFunctionOptions } from './types';

/**
 * A hook that provides a debounced callback function with state tracking.
 * Debouncing ensures a function is called after N milliseconds of inactivity
 *
 * @template T - The type of value being debounced
 * @param {DeferredFunction<T>} func - The function to debounce
 * @param {number} [wait=500] - The number of milliseconds to delay
 * @param {DeferredFunctionOptions} [options] - Additional options for debouncing behavior
 * @returns {[(value: T) => void, boolean]} - A tuple containing:
 *   - The debounced callback function
 *   - A boolean indicating if the callback is currently debounced
 *
 * @example
 * // Debounced window resize handler
 * const [handleResize, isDebounced] = useDebounceCallback((width) => {
 *   console.log('Window width:', width);
 * });
 * window.addEventListener('resize', () => handleResize(window.innerWidth));
 *
 * // Debounced search input handler
 * const [handleSearch, isDebounced] = useDebounceCallback((query) => {
 *   console.log('Searching for:', query);
 * });
 * <input onChange={(e) => handleSearch(e.target.value)} />
 */
export function useDebounceCallback<T>(
  func: DeferredFunction<T>,
  wait = DEFAULT_DEBOUNCE_DURATION,
  options?: DeferredFunctionOptions
): [(value: T) => void, boolean] {
  const [isDebounced, setIsDebounced] = useState(false);
  const funcRef = useRef(func);
  funcRef.current = func;

  const debouncedUpdate = useDebouncedCallbackOriginal(
    (value: T) => {
      funcRef.current(value);
      setIsDebounced(false);
    },
    wait,
    options
  );

  const callback = useCallback(
    (value: T) => {
      if (value) {
        setIsDebounced(true);
        debouncedUpdate(value);
      } else {
        setIsDebounced(false);
        debouncedUpdate.cancel();
        requestAnimationFrame(() => funcRef.current(value));
      }
    },
    [debouncedUpdate]
  );

  // cleanup the debounced callback if the component unmounts
  useEffect(() => () => debouncedUpdate?.cancel(), [debouncedUpdate]);

  return [callback, isDebounced];
}
const DEFAULT_DEBOUNCE_DURATION = 500;
